import { useState, useEffect } from 'react'
import { getLogoUrl } from '../utils/logoHelpers'
import { teamInitials } from './TeamAvatar'
import useInsightsPreferences from '../hooks/useInsightsPreferences'

// Fallback avatar colors, picked by hashing the name so an org keeps the same
// color across pages. Deliberately not the chart palette, which is pinned to
// metrics-builder-mfa.
const FALLBACK_COLORS = [
  '#0f766e',
  '#7c3aed',
  '#b45309',
  '#be123c',
  '#1d4ed8',
  '#15803d',
  '#a21caf',
  '#0e7490'
]

const colorFor = (seed: string): string => {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  return FALLBACK_COLORS[(hash >>> 0) % FALLBACK_COLORS.length]
}

const SIZES = {
  sm: { box: 'h-5 w-5 rounded', text: 'text-[9px]', api: 64 },
  md: { box: 'h-7 w-7 rounded', text: 'text-[11px]', api: 64 },
  lg: { box: 'h-12 w-12 rounded', text: 'text-sm', api: 128 },
  xl: {
    box: 'h-14 w-14 md:h-20 md:w-20 rounded-lg',
    text: 'text-xl md:text-2xl',
    api: 128
  }
}

// Company logo fetched from logo.dev, with a colored initials avatar as fallback.
interface Props {
  website?: string | null
  name?: string | null
  variant?: 'sm' | 'md' | 'lg' | 'xl'
}

const CompanyLogo: React.FC<Props> = ({ website, name, variant = 'sm' }) => {
  const [imgError, setImgError] = useState(false)
  const {
    prefs: { hideOrgLogos }
  } = useInsightsPreferences()

  useEffect(() => {
    setImgError(false)
  }, [website, hideOrgLogos])

  const { box, text, api } = SIZES[variant]
  const logoUrl = getLogoUrl(website, api)

  if (hideOrgLogos) {
    return null
  }

  if (!logoUrl || imgError) {
    return (
      <div
        role='img'
        aria-label={name ? `${name} logo` : 'Company logo'}
        className={`${box} ${text} flex items-center justify-center shrink-0 font-semibold text-white`}
        style={{ backgroundColor: colorFor(name ?? '') }}
      >
        {teamInitials(name ?? '') || '?'}
      </div>
    )
  }

  return (
    <img
      src={logoUrl}
      alt={name ? `${name} logo` : 'Company logo'}
      className={`${box} object-contain shrink-0`}
      loading='lazy'
      decoding='async'
      referrerPolicy='no-referrer'
      onError={() => setImgError(true)}
    />
  )
}

export default CompanyLogo
